import Fuse from "fuse.js";
import { useMemo } from "react";

import { SearchBox, useSearch } from "@transfr-inc/dashboard-components/forms";

const matchesMap = {
  "instructors.fullName": "instructor",
};

import "./classrooms.searchbox.scss";

export function ClassroomsSearchbox({
  onSearch,
  data,
  filters,
  searchTerm,
  onSearchTermChange,
}) {
  const fuse = useMemo(() => {
    const fuseData = data.map((classroom) => ({
      ...classroom,
      instructors: classroom.instructors?.map(({ firstName, lastName }) => ({
        fullName: `${lastName}, ${firstName}`,
      })),
      products: classroom.products?.map((product) => String(product)),
    }));

    const fuseOptions = {
      includeMatches: true,
      // Get results closer to a perfect match
      threshold: 0.3,
      // To allow advanced search using query syntax
      useExtendedSearch: true,
      keys: [
        { name: "name", weight: 2 },
        "instructors.fullName",
        "products",
        "organizationCode",
      ],
    };

    const fuseIndex = Fuse.createIndex(fuseOptions.keys, fuseData);
    return new Fuse(fuseData, fuseOptions, fuseIndex);
  }, [data]);

  const search = useSearch({
    data,
    queryFn: (_, searchTerm) => {
      // AND conditions will be added to the query only if they are present
      const andConditions = [];
      // OR conditions will give priority to exact matches
      const orConditions = [
        { name: `="${searchTerm}"|"${searchTerm}"` },
        { "instructors.fullName": `="${searchTerm}"|"${searchTerm}"` },
      ];

      // Add product filter to the query if present
      if (filters?.products?.length > 0) {
        andConditions.push({ products: filters.products.join("|") });
      }

      // Add organization filter to the query if present
      if (filters?.organization) {
        andConditions.push({ organizationCode: filters.organization.code });
      }

      // Build the query for the fuse search
      const query = {
        $and: [
          ...andConditions,
          {
            $or: orConditions,
          },
        ],
      };

      return fuse.search(query).map((r) => {
        return {
          ...r,
          // Remove keys that are not in the matchesMap from the matches
          matches: r.matches.filter((m) => !!matchesMap[m.key]),
        };
      });
    },
  });

  const onSearchHandler = (term) => {
    onSearchTermChange?.(term);
    const results = term ? search.queryData(term) : undefined;
    const ids = results?.map((r) => r.item.id);
    onSearch({ classroomIds: ids });
  };

  const onResultSelectedHandler = (option) => {
    const classroomId = option?.item?.id;
    onSearch({ classroomIds: [classroomId] });
  };

  return (
    <SearchBox
      className="classrooms-searchbox"
      placeholder={"Search"}
      data={data}
      query={search.queryData}
      searchTerm={searchTerm}
      onSearch={onSearchHandler}
      onSearchTermChange={onSearchHandler}
      onResultSelected={onResultSelectedHandler}
      customOptionLabel={(r) => r?.item?.name}
      optionComponent={(r) => (
        <div className="classrooms-searchbox-option">
          <div className="classrooms-searchbox-option-name">
            {r?.item?.name}
          </div>
          {r?.matches?.length > 0 &&
            r?.matches?.map((m) => (
              <div
                className="classrooms-searchbox-option-matches"
                key={m.refIndex}
              >
                {matchesMap[m.key]}: {m.value}
              </div>
            ))}
        </div>
      )}
    />
  );
}
